/**
 * PersonaChatArtifactViewer — renders a persisted persona-chat conversation
 * as a stack of MessageBubbles. Each bubble's phase trail auto-renders
 * from `message.metadata.phase_events` via the PersistedPhaseTrail
 * already wired into MessageBubble — no extra plumbing needed.
 *
 * Content shape accepted:
 *   { messages: Message[], persona_name?: string, session_id?: string,
 *     created_at?: string, transcript_markdown?: string }
 *
 * Falls back to rendering the transcript_markdown (if present) or the
 * raw JSON when the messages array is missing / malformed, so an
 * unexpected shape doesn't leave the user staring at a blank viewer.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Artifact } from '../../../types/artifact';
import { Message } from '../../../contexts/ConversationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { MessageBubble } from '../../conversation/MessageBubble';

interface PersonaChatArtifactViewerProps {
  artifact: Artifact;
}

interface PersonaChatContent {
  messages?: Message[];
  persona_name?: string;
  session_id?: string;
  created_at?: string;
  transcript_markdown?: string;
}

export const PersonaChatArtifactViewer: React.FC<PersonaChatArtifactViewerProps> = ({
  artifact,
}) => {
  const { darkMode } = useTheme();

  // Normalize content — backend may return string or object depending on
  // how the artifact was created.
  const content: PersonaChatContent =
    typeof artifact.content === 'string'
      ? { transcript_markdown: artifact.content }
      : (artifact.content as PersonaChatContent) || {};

  const messages = Array.isArray(content.messages) ? content.messages : [];
  const personaName = content.persona_name || artifact.persona_id || 'persona';
  const visibleMessages = messages.filter(
    (m) => m && (m.role === 'user' || m.role === 'assistant' || m.role === 'error'),
  );

  if (visibleMessages.length === 0) {
    // Fall back to transcript_markdown (plain ReactMarkdown) or raw JSON
    // rather than rendering an empty scroll.
    if (content.transcript_markdown) {
      return (
        <div className={`prose prose-sm max-w-none ${darkMode ? 'prose-invert' : ''}`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // LLM-generated transcript — links open in new tabs so
              // navigating doesn't yank the user out of the artifact viewer.
              a: ({ node, href, children, ...props }) => (
                <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                  {children}
                </a>
              ),
            }}
          >
            {content.transcript_markdown}
          </ReactMarkdown>
        </div>
      );
    }
    return (
      <pre
        className={`whitespace-pre-wrap text-xs p-4 rounded ${darkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-800'}`}
      >
        {JSON.stringify(artifact.content, null, 2)}
      </pre>
    );
  }

  return (
    <div className={`persona-chat-artifact-viewer ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} rounded p-2 -m-2`}>
      <div
        className={`text-xs mb-3 px-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
      >
        {visibleMessages.length} messages
        {content.created_at ? ` · captured ${new Date(content.created_at).toLocaleString()}` : ''}
        {content.session_id ? ` · session ${content.session_id.slice(0, 8)}` : ''}
      </div>
      {visibleMessages.map((m, idx) => (
        <MessageBubble
          key={m.id || idx}
          message={m}
          showTimestamp={true}
          personaName={personaName}
          modelName={(m.metadata as any)?.model_used || (m.metadata as any)?.model}
        />
      ))}
    </div>
  );
};
